'use client';

import * as React from 'react';

import { Eye, EyeOff } from 'lucide-react';
import { InputPrefixSuffixCds } from '@/components/cds/input-prefix-suffix-cds';
import { IconClientRendererCdsType } from '@/components/cds/icon-client-renderer-cds';

/**
 * Props for the InputPasswordCds component.
 *
 * @typedef {Object} InputPasswordCdsProps
 * @property {string} [labelShowPassword] - Label to display when the password is hidden.
 * @property {string} [labelHidePassword] - Label to display when the password is visible.
 * @property {LucideIcon} [iconShowPassword] - Icon to display when the password is hidden.
 * @property {LucideIcon} [iconHidePassword] - Icon to display when the password is visible.
 * @extends {React.ComponentProps<'input'>}
 */
export type InputPasswordCdsProps = React.ComponentProps<'input'> & {
  labelShowPassword?: string;
  labelHidePassword?: string;
  iconShowPassword?: IconClientRendererCdsType;
  iconHidePassword?: IconClientRendererCdsType;
};

/**
 * A password input component with a toggle to show or hide the password.
 *
 * @component
 * @param {string} labelShowPassword - The label for the button to show the password. Defaults to 'Show password'.
 * @param {string} labelHidePassword - The label for the button to hide the password. Defaults to 'Hide password'.
 * @param {React.ElementType} iconShowPassword - The icon to display when the password is hidden. Defaults to `Eye`.
 * @param {React.ElementType} iconHidePassword - The icon to display when the password is visible. Defaults to `EyeOff`.
 * @param {InputPasswordCdsProps} props - Additional props to pass to the input component.
 * @param {React.Ref<HTMLInputElement>} ref - The ref to be forwarded to the input element.
 *
 * @returns {JSX.Element} The rendered password input component.
 * @example
 * ```tsx
 * <InputPasswordCds />
 * ```
 * @example
 * ```tsx
 *  <InputPasswordCds className="text-sm" placeholder="Enter your password" required />
 * ```
 */
const InputPasswordCds = React.forwardRef<HTMLInputElement, InputPasswordCdsProps>(
  ({ labelShowPassword, labelHidePassword, iconShowPassword, iconHidePassword, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const IconShow = iconShowPassword || Eye;
    const IconHide = iconHidePassword || EyeOff;
    const labelShow = labelShowPassword || 'Show password';
    const labelHide = labelHidePassword || 'Hide password';

    return (
      <InputPrefixSuffixCds
        {...props}
        type={showPassword ? 'text' : 'password'}
        ref={ref}
        suffixIcon={showPassword ? IconHide : IconShow}
        suffixIconProps={{ 'aria-label': showPassword ? labelHide : labelShow }}
        suffixClassName="size-[1rem] right-[0.65rem]"
        onSuffixClick={togglePasswordVisibility}
        className="pr-[2rem]"
      />
    );
  }
);
InputPasswordCds.displayName = 'InputPasswordCds';

export { InputPasswordCds };
